import { findWinningParticipant } from './challengeWinner';
import { challengePhase, ChallengePhase, isTerminalPhase } from './challengePhase';
import { GUEST_USER_ID } from './legacyMockUsers';

export const PLAYER_STATS_STORAGE_KEY = 'lockedin_player_stats';
const STATS_KEY_PREFIX = 'lockedin_player_stats_';

export function playerStatsStorageKey(uid) {
  if (!uid || uid === GUEST_USER_ID) return null;
  return `${STATS_KEY_PREFIX}${uid}`;
}

/** @deprecated use playerStatsStorageKey(uid) — legacy global key */
export function loadPlayerStats(uid = null) {
  const key = uid ? playerStatsStorageKey(uid) : PLAYER_STATS_STORAGE_KEY;
  if (!key) return {};
  try {
    const raw = window.localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch (_) {}
  return {};
}

export function savePlayerStats(uid, stats) {
  const key = uid ? playerStatsStorageKey(uid) : PLAYER_STATS_STORAGE_KEY;
  if (!key || !stats) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(stats));
  } catch (_) {}
}

/**
 * @returns {boolean} whether a bet counts toward public W-L / streak.
 *
 * Void terminal phases — declined, refunded, expired — are excluded through the
 * canonical phase predicate rather than a hand-maintained status denylist, so
 * newly-added terminal statuses (e.g. EXPIRED from WI-3) can never silently leak
 * into a player's record. The positive "actually settled" confirmation stays
 * status-driven: a settlement object on a still-locked challenge does not count
 * until the status settles or a winner is determined.
 */
export function isSettledStatsBet(bet) {
  if (!bet?.participants?.length || !bet.settlement) return false;
  const phase = challengePhase(bet);
  if (isTerminalPhase(phase) && phase !== ChallengePhase.SETTLED) return false;
  return bet.status === 'SETTLED' || Boolean(bet.finalOutcome);
}

/**
 * Stable W-L / streak from fully settled challenges only (no active/pending).
 * @param {object[]} bets
 */
export function computePlayerStatsFromSettledBets(bets) {
  const settled = (bets || [])
    .filter(isSettledStatsBet)
    .sort(
      (a, b) =>
        (a.updatedAt ?? a.createdAt ?? 0) - (b.updatedAt ?? b.createdAt ?? 0)
    );

  let stats = {};
  settled.forEach((bet) => {
    stats = applyBetResultToStats(stats, bet, bet.settlement);
  });
  return stats;
}

/** @returns {boolean} whether this participant won the settled bet */
export function didParticipantWin(participant, bet, resolutionOrWinner) {
  if (!participant?.id) return false;
  if (resolutionOrWinner?.isDraw) return false;

  if (resolutionOrWinner?.winningPlayerId) {
    return participant.id === resolutionOrWinner.winningPlayerId;
  }

  const label =
    typeof resolutionOrWinner === 'string'
      ? resolutionOrWinner
      : resolutionOrWinner?.winningTeam;
  if (!label) return false;

  const winner = findWinningParticipant(bet, label);
  return winner?.id === participant.id;
}

/**
 * Apply win/loss/streak for every participant on a settled bet (live or custom).
 * @param {Record<string, { wins: number, losses: number, currentStreak: number }>} stats
 * @param {object} bet
 * @param {object | string} resolutionOrWinner — resolution object or winning pick/team label
 */
export function applyBetResultToStats(stats, bet, resolutionOrWinner) {
  if (!bet?.participants?.length || resolutionOrWinner?.isDraw) return stats;

  const next = { ...stats };
  bet.participants.forEach((p) => {
    if (!p?.id || p.id === GUEST_USER_ID) return;
    const current = next[p.id] ?? { wins: 0, losses: 0, currentStreak: 0 };
    const won = didParticipantWin(p, bet, resolutionOrWinner);
    next[p.id] = {
      wins: current.wins + (won ? 1 : 0),
      losses: current.losses + (won ? 0 : 1),
      currentStreak: won ? (current.currentStreak ?? 0) + 1 : 0,
    };
  });
  return next;
}
